/**
 * Sub-component of a <Dialog> component for editing a client's profile.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {() => void} props.onClose - Function to call when the dialog is closed.
 * @param {() => void} props.onSaveData - Function to call when the data is saved.
 * @param {string} props.firstName - The first name of the client.
 * @param {(value: string) => void} props.setFirstName - Function to set the first name of the client.
 * @param {string} props.lastName - The last name of the client.
 * @param {(value: string) => void} props.setLastName - Function to set the last name of the client.
 * @param {string} props.email - The email address of the client.
 * @param {(value: string) => void} props.setEmail - Function to set the email address of the client.
 * @param {number | undefined} props.birthYear - The birth year of the client.
 * @param {(value: number | undefined) => void} props.setBirthYear - Function to set the birth year of the client.
 * @param {Enum_Gender_Enum | undefined} props.gender - The gender of the client.
 * @param {(value: Enum_Gender_Enum | undefined) => void} props.setGender - Function to set the gender of the client.
 * @param {Enum_Racial_Ethnic_Enum | undefined} props.racialEthnic - The race/ethnicity of the client.
 * @param {(value: Enum_Racial_Ethnic_Enum | undefined) => void} props.setRacialEthnic - Function to set the race/ethnicity of the client.
 * @param {string} props.presentingProblem - The presenting problem of the client.
 * @param {(value: string) => void} props.setPresentingProblem - Function to set the presenting problem of the client.
 * @returns {JSX.Element} The AddEditClient component.
 */

import { faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles, MenuItem, Typography } from "@material-ui/core"
import { FC, useState } from "react"
import {
  Enum_Gender_Enum,
  Enum_Racial_Ethnic_Enum,
} from "../../generated/graphql"
import { colors } from "../../utils/theme"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import Input from "../atoms/input/Input"

interface AddEditClientProps {
  onClose: () => void
  onSaveData: () => void
  firstName: string
  setFirstName: (value: string) => void
  lastName: string
  setLastName: (value: string) => void
  email: string
  birthYear: number | undefined
  setBirthYear: (value: number | undefined) => void
  gender: Enum_Gender_Enum | undefined
  setGender: (value: Enum_Gender_Enum | undefined) => void
  racialEthnic: Enum_Racial_Ethnic_Enum | undefined
  setRacialEthnic: (value: Enum_Racial_Ethnic_Enum | undefined) => void
  presentingProblem: string
  setPresentingProblem: (value: string) => void
}

const AddEditClient: FC<AddEditClientProps> = (props) => {
  const classes = useStyles()
  const [birthYearError, setBirthYearError] = useState<string | undefined>(
    undefined
  )
  const [genderError, setGenderError] = useState<string | undefined>(undefined)
  const [racialEthnicError, setRacialEthnicError] = useState<
    string | undefined
  >(undefined)

  const close = () => {
    setBirthYearError(undefined)
    setGenderError(undefined)
    setRacialEthnicError(undefined)
    props.onClose()
  }

  const formatString = (str: String) => {
    return str
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize the first letter of each word
  }

  // Only allow digits in the birth year input
  const validateAndSetBirthYear = (newBirthYear: string) => {
    if (/^\d*$/.test(newBirthYear)) {
      props.setBirthYear(
        newBirthYear.length > 0 ? parseInt(newBirthYear) : undefined
      ) // Update state if the value is a valid number
    }
  }

  return (
    <div className={classes.dialogContent}>
      <div className={classes.dialogCloseContainer}>
        <button className={classes.closeButton} onClick={close}>
          <FontAwesomeIcon icon={faXmark} className={classes.closeIcon} />
        </button>
      </div>
      <Typography variant="h3" className={classes.dialogTitle}>
        Edit Client Profile
      </Typography>
      <Typography variant="body1" className={classes.dialogSubtitle}>
        Add to or edit your client's profile
      </Typography>

      <div className={classes.inputContainer}>
        <Input
          label="First Name"
          value={props.firstName}
          onChange={(event) => props.setFirstName(event.target.value)}
          className={classes.dialogInput}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <div className={classes.spacer} />
        <Input
          label="Last Name"
          value={props.lastName}
          onChange={(event) => props.setLastName(event.target.value)}
          className={classes.dialogInput}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <div className={classes.spacer} />
        <Input
          disabled={true}
          label="Email"
          contentEditable={false}
          value={props.email}
          className={classes.dialogInput}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <div className={classes.spacer} />
        <div className={classes.flexRow}>
          <Input
            label="Birth Year"
            value={props.birthYear?.toString() ?? ""}
            onChange={(event) => validateAndSetBirthYear(event.target.value)}
            className={classes.dialogInput}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              inputMode: "numeric",
            }}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            error={Boolean(birthYearError)}
            helperText={birthYearError}
          />
          <div className={classes.horizontalSpacer} />
          <Input
            label="Gender"
            value={props.gender || ""}
            select={true}
            className={classes.dialogInput}
            InputLabelProps={{
              shrink: true,
            }}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            onChange={(event) => {
              props.setGender(
                event.target.value as Enum_Gender_Enum | undefined
              )
            }}
            error={Boolean(genderError)}
            helperText={genderError}
          >
            {Object.values(Enum_Gender_Enum).map((gender) => (
              <MenuItem key={gender} value={gender}>
                {formatString(gender)}
              </MenuItem>
            ))}
            <MenuItem key={"undefined"} value={undefined}>
              <div
                style={{
                  display: "flex",
                  marginRight: 8,
                  paddingTop: 2,
                }}
              >
                <FontAwesomeIcon icon={faXmark} className={classes.closeIcon} />
              </div>
              Clear selection
            </MenuItem>
          </Input>
          <div className={classes.horizontalSpacer} />
          <Input
            label="Race/Ethnicity"
            value={props.racialEthnic || ""}
            select={true}
            variant="outlined"
            className={classes.dialogInput}
            InputLabelProps={{
              shrink: true,
            }}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            onChange={(event) => {
              props.setRacialEthnic(
                event.target.value as Enum_Racial_Ethnic_Enum | undefined
              )
            }}
            error={Boolean(racialEthnicError)}
            helperText={racialEthnicError}
          >
            {Object.values(Enum_Racial_Ethnic_Enum).map((racialEthnic) => (
              <MenuItem key={racialEthnic} value={racialEthnic}>
                {formatString(racialEthnic)}
              </MenuItem>
            ))}
            <MenuItem key={"undefined"} value={undefined}>
              <div
                style={{
                  display: "flex",
                  marginRight: 8,
                  paddingTop: 2,
                }}
              >
                <FontAwesomeIcon icon={faXmark} className={classes.closeIcon} />
              </div>
              Clear selection
            </MenuItem>
          </Input>
        </div>
        <div className={classes.spacer} />
        <Input
          label="Presenting Problem"
          placeholder="Presenting Problem"
          value={props.presentingProblem}
          onChange={(event) => props.setPresentingProblem(event.target.value)}
          className={classes.dialogInput}
          InputLabelProps={{
            shrink: true,
          }}
          FormHelperTextProps={{
            className: classes.helperText,
          }}
          multiline
          minRows={3}
          maxRows={3}
        />
      </div>

      <PrimaryButton
        onClick={async () => {
          setBirthYearError(undefined)
          setGenderError(undefined)
          setRacialEthnicError(undefined)
          let errors = false
          if (
            props.birthYear !== undefined &&
            (props.birthYear < 1900 ||
              props.birthYear > new Date().getFullYear())
          ) {
            setBirthYearError("Please enter a valid birth year.")
            errors = true
          }
          if (errors) {
            return
          }
          props.onSaveData()
        }}
        className={classes.dialogButton}
        type="submit"
      >
        Confirm and Save
      </PrimaryButton>
      <div className={classes.cancelContainer}>
        <GhostButton onClick={close}>Cancel</GhostButton>
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  closeIcon: {
    fontSize: 20,
    color: "#737373",
  },
  closeButton: {
    backgroundColor: "rgba(0,0,0,0.03)",
    width: 32,
    height: 32,
    border: 0,
    borderRadius: 32,
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
  dialogTitle: {
    color: colors.neutral.denim,
    marginBottom: 12,
  },
  dialogSubtitle: {
    color: "#737373",
    marginBottom: theme.spacing(3),
  },
  dialogInput: {
    width: "100%",
    height: "100%",
    backgroundColor: colors.neutral.white,
  },
  dialogButton: {
    width: "100%",
  },
  cancelContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  successText: {
    textAlign: "center",
  },
  inputContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#FCFCFC",
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  spacer: {
    marginBottom: theme.spacing(3),
  },
  flexRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  horizontalSpacer: {
    marginRight: theme.spacing(1),
  },
  label: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
  helperText: {
    backgroundColor: "#FCFCFC",
    margin: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}))

export default AddEditClient
